import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Keyboard, ScrollView, StatusBar } from "react-native";
import { Box, Loading } from "../../components/common";
import { openConversationRequest } from "@microdoing/shared/services/message/actions";
import { getUserId } from "@microdoing/shared/services/user/selectors";
import {
  getExternalProfileAchievements,
  getExternalProfileBestStreak,
  getExternalProfileBio,
  getExternalProfileCategories,
  getExternalProfileFirstName,
  getExternalProfileId,
  getExternalProfileJobDescription,
  getExternalProfileLastName,
  getExternalProfileNotificationRequesting,
  getExternalProfilePicture,
  getExternalProfilePulseSent,
  getExternalProfileRequesting,
  getExternalProfileSkills,
  getExternalProfileTotalDoneActions,
  getExternalProfilembti,
} from "@microdoing/shared/services/externalProfile/selectors";
import { sendPulseNotification } from "@microdoing/shared/services/user/actions";
import { unsetExternalUser } from "@microdoing/shared/services/externalProfile/actions";
import { showAlert } from "@microdoing/shared/services/profileSettings/actions";
import i18n from "@microdoing/shared/i18n";
import UserStats from "./UserStats/UserStats";
import ProfileHeader from "./ProfileHeader";
import { isWebDesktop } from "@microdoing/shared/utils/deviceDetectors";
import WebDesktopView from "app/components/common/Web/WebDesktopView";
import theme from "app/Theme/Theme";
import { breadCrumbsExternalProfileScreen } from "app/navigation/breadcrumb/BreadcrumbPaths";
import Badges from "./Badges";
import { useNavigation } from "@react-navigation/native";
import HeaderScreen from "app/components/common/HeaderScreen";
import {
  SafeAreaView,
  useSafeAreaInsets,
} from "react-native-safe-area-context";

const ExternalProfile = () => {
  const [isPulseAnimated, setIsPulseAnimated] = useState(false);

  const requesting = useSelector(getExternalProfileRequesting);
  const notificationRequesting = useSelector(
    getExternalProfileNotificationRequesting,
  );
  const categories = useSelector(getExternalProfileCategories);
  const friendId = useSelector(getExternalProfileId);
  const userId = useSelector(getUserId);
  const firstName = useSelector(getExternalProfileFirstName);
  const picture = useSelector(getExternalProfilePicture);
  const lastName = useSelector(getExternalProfileLastName);
  const skills = useSelector(getExternalProfileSkills);
  const bio = useSelector(getExternalProfileBio);
  const mbti = useSelector(getExternalProfilembti);
  const jobDescription = useSelector(getExternalProfileJobDescription);
  const bestStreak = useSelector(getExternalProfileBestStreak);
  const pulseSent = useSelector(getExternalProfilePulseSent);
  const totalDoneActions = useSelector(getExternalProfileTotalDoneActions);
  const achievements = useSelector(getExternalProfileAchievements);

  const insets = useSafeAreaInsets();

  const dispatch = useDispatch();
  const navigation = useNavigation();

  useEffect(() => {
    Keyboard.dismiss();
  }, []);

  useEffect(() => {
    if (notificationRequesting) {
      setTimeout(() => {
        dispatch(
          showAlert({
            title: i18n.t("send-pulse-success-title"),
            message: i18n.t("send-pulse-success-message"),
            alertType: "success",
          }),
        );
      }, 2000);
    }
  }, [notificationRequesting]);

  const goBack = () => {
    navigation.goBack();
    dispatch(unsetExternalUser());
  };

  const handleSendPulseNotification = () => {
    setIsPulseAnimated(true);

    dispatch(
      sendPulseNotification({
        userId: userId,
        friendId: friendId,
      }),
    );
    setTimeout(() => {
      setIsPulseAnimated(false);
    }, 2000);
  };

  const onChatOpen = () => {
    const receivers = [
      {
        _id: friendId,
        name: `${firstName} ${lastName}`,
        picture: picture,
      },
    ];

    dispatch(openConversationRequest({ receivers }));
  };

  const getTotalSkillsPoints = () => {
    const total = skills.reduce((prev, curr) => {
      return prev + curr.experience;
    }, 0);

    return total;
  };

  if (isWebDesktop) {
    return (
      <WebDesktopView breadcrumbsPath={breadCrumbsExternalProfileScreen}>
        <>
          {!!requesting && <Loading size={35} />}
          <ScrollView
            contentContainerStyle={styles.scrollView}
            style={{
              height: "100%",
              flex: 1,
              backgroundColor: "transparent",
            }}
          >
            <Box flex={1}>
              <ProfileHeader
                onChatOpen={onChatOpen}
                sendPulseNotification={handleSendPulseNotification}
                isPulseAnimated={isPulseAnimated}
                picture={picture}
                bio={bio}
                mbti={mbti}
                jobDescription={jobDescription}
                name={`${firstName} ${lastName}`}
                streak={bestStreak}
                pulseSent={pulseSent}
                actions={totalDoneActions}
                isUserProfile={false}
                externalUserId={friendId}
              />
            </Box>
            <Box flex={1}>
              <UserStats
                skills={skills}
                hasGoals={false}
                categories={categories}
                totalSkillsPoints={getTotalSkillsPoints()}
              />
              <Badges achievements={achievements} />
            </Box>
          </ScrollView>
        </>
      </WebDesktopView>
    );
  }

  return (
    <Box
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      backgroundColor="neutral.75"
      paddingHorizontal="sm"
      flex={1}
      style={{ marginTop: insets.top }}
    >
      <HeaderScreen leftIcon title="" />
      <StatusBar />
      {!!requesting && <Loading size={35} />}
      <ScrollView
        style={styles.scrollViewContainer}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.scrollView}
      >
        <Box
          width="100%"
          alignSelf="center"
          justifyContent="center"
          flexDirection="column"
          style={styles.container}
        >
          <ProfileHeader
            onChatOpen={onChatOpen}
            sendPulseNotification={handleSendPulseNotification}
            isPulseAnimated={isPulseAnimated}
            picture={picture}
            bio={bio}
            mbti={mbti}
            jobDescription={jobDescription}
            name={`${firstName} ${lastName}`}
            streak={bestStreak}
            pulseSent={pulseSent}
            actions={totalDoneActions}
            isUserProfile={false}
            externalUserId={friendId}
          />
          <Box>
            <UserStats
              skills={skills}
              hasGoals={false}
              categories={categories}
              totalSkillsPoints={getTotalSkillsPoints()}
            />
            <Badges achievements={achievements} />
          </Box>
        </Box>
      </ScrollView>
    </Box>
  );
};

const styles = {
  scrollView: {
    paddingTop: isWebDesktop ? 60 : 50,
    backgroundColor: theme.colors["neutral.75"],
    justifyContent: "center",
    alignItems: "flex-start",
    display: "flex",
    flexDirection: isWebDesktop ? "row" : "column",
    columnGap: isWebDesktop ? theme.spacing.xm : 0,
  },
  scrollViewContainer: {
    width: "100%",
  },
  container: {
    marginBottom: 100,
  },
  backIcon: {
    position: "absolute",
    top: 20,
    left: 20,
  },
};

export default ExternalProfile;
